import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getFollowingStories } from "../../functions/story";
import CreateStoryCard from "./CreateStoryCard";
import StoryCard from "./StoryCard";
import StoryViewer from "./StoryViewer";
import CreateStoryModal from "./CreateStoryModal";
import { useSocketContext } from "../../context/SocketContext";
//import Stories from "../../stories"; // Adjust the path if needed
import "./style.css";

export default function HomeMiddle() {
  const { user } = useSelector((state) => ({ ...state }));
  const { socket, isConnected } = useSocketContext();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [isUploadingStory, setIsUploadingStory] = useState(false);
  const storiesContainerRef = useRef(null);
  const isBusyRef = useRef(false);

  // Keep global flag so socket handlers skip during story upload/modal open
  useEffect(() => {
    isBusyRef.current = showCreateStory || isUploadingStory;
    if (typeof window !== "undefined") {
      window.__storyUploadOrModalInProgress = isBusyRef.current;
    }
    return () => {
      if (typeof window !== "undefined") window.__storyUploadOrModalInProgress = false;
    };
  }, [showCreateStory, isUploadingStory]);

  useEffect(() => {
    fetchStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for real-time story creation and deletion events from friends
  useEffect(() => {
    if (!socket || !isConnected || !user?.id) {
      return;
    }

    // Listen for story_created event
    const handleStoryCreatedSocket = (data) => {
      if (isBusyRef.current) return; // Don't refresh while user is uploading or modal open
      console.log('New story created by friend:', data);
      fetchStories(true);
    };

    // Listen for story_deleted event
    const handleStoryDeleted = (data) => {
      if (isBusyRef.current) return; // Don't refresh while user is uploading or modal open
      console.log('Story deleted by friend:', data);
      fetchStories(true);
    };

    socket.on('story_created', handleStoryCreatedSocket);
    socket.on('story_deleted', handleStoryDeleted);

    // Cleanup listeners on unmount
    return () => {
      socket.off('story_created', handleStoryCreatedSocket);
      socket.off('story_deleted', handleStoryDeleted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected, user?.id]);

  const fetchStories = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await getFollowingStories(user.token);
      if (response.stories) {
        const list = response.stories.filter(
          (userStories) =>
            userStories.stories &&
            Array.isArray(userStories.stories) &&
            userStories.stories.length > 0
        );
        const ownIndex = list.findIndex((s) => (s.user?._id || s.user?.id) === (user.id || user._id));
        if (ownIndex > -1) {
          const own = list[ownIndex];
          const others = list.filter((_, idx) => idx !== ownIndex);
          setStories([own, ...others]);
        } else {
          setStories(list);
        }
      }
    } catch (error) {
      console.error("Error fetching stories:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleStoryClick = (userStories, storyIndex = 0) => {
    setSelectedStory(userStories);
    setCurrentStoryIndex(storyIndex);
  };

  const handleCloseViewer = () => {
    setSelectedStory(null);
    setCurrentStoryIndex(0);
  };

  const handleNextStory = () => {
    if (selectedStory && currentStoryIndex < selectedStory.stories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
    } else {
      // Move to next user's stories
      const currentUserIndex = stories.findIndex(
        (s) => s.user._id === selectedStory.user._id
      );
      if (currentUserIndex < stories.length - 1) {
        const nextUserStories = stories[currentUserIndex + 1];
        setSelectedStory(nextUserStories);
        setCurrentStoryIndex(0);
      } else {
        handleCloseViewer();
      }
    }
  };

  const handlePrevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
    } else {
      // Move to previous user's stories
      const currentUserIndex = stories.findIndex(
        (s) => s.user._id === selectedStory.user._id
      );
      if (currentUserIndex > 0) {
        const prevUserStories = stories[currentUserIndex - 1];
        setSelectedStory(prevUserStories);
        setCurrentStoryIndex(prevUserStories.stories.length - 1);
      }
    }
  };

  const handleStoryCreated = async (newStory) => {
    setShowCreateStory(false);
    setIsUploadingStory(false);
    // Silent refetch so home page shows new story without loading flash (no "Loading vibe...")
    await fetchStories(true);
    requestAnimationFrame(() => {
      storiesContainerRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    });
  };

  if (loading) {
    return (
      <div className="stories_container">
        <div className="stories_loading">Loading vibe...</div>
      </div>
    );
  }

  return (
    <div className="home_middle">
      <div className="stories_container" ref={storiesContainerRef}>
        <div className="stories_wrapper">
          {/* Create Vibe Card */}
          <CreateStoryCard
            user={user}
            onClick={() => setShowCreateStory(true)}
          />

          {/* User Stories */}
          {stories.map((userStories, index) => {
            // Determine if the current user has unseen stories for this user
            const hasUnseen = (userStories.stories || []).some((s) => {
              // If story has no viewers or current user id not in viewers, it's unseen
              if (!s.viewers || s.viewers.length === 0) return true;
              return !s.viewers.some((v) => v.user?._id === (user.id || user._id));
            });

            return (
              <StoryCard
                key={userStories.user?._id || index}
                userStories={userStories}
                onClick={() => handleStoryClick(userStories)}
                isOwn={userStories.user?._id === user.id}
                hasUnseen={hasUnseen}
              />
            );
          })}
        </div>
      </div>

      {/* Story Viewer */}
      {selectedStory && (
        <StoryViewer
          userStories={selectedStory}
          currentIndex={currentStoryIndex}
          onClose={handleCloseViewer}
          onNext={handleNextStory}
          onPrev={handlePrevStory}
          user={user}
          onStoryDeleted={fetchStories}
        />
      )}

      {/* Create Vibe Modal */}
      {showCreateStory && (
        <CreateStoryModal
          user={user}
          onClose={() => {
            setShowCreateStory(false);
            setIsUploadingStory(false); // Ensure refresh can resume if modal closed during upload
          }}
          onStoryCreated={handleStoryCreated}
          onUploadStart={() => setIsUploadingStory(true)}
          onUploadEnd={() => setIsUploadingStory(false)}
        />
      )}
    </div>
  );
}
